'use client';

/**
 * DataGridSavedViews — Phase F1 Sprint 2.
 *
 * Toolbar dropdown for named, saved DataGrid views. A view captures the
 * full set of "what the user is looking at" state — search + filters +
 * sort + page size + column order/visibility/density — so power users
 * can build a worklist once and return to it across sessions/devices.
 *
 * Consumer flow:
 *   1. Hold the view state somewhere (typically `useDataGridViews` hook
 *      backed by `useUserPreferences`, mirroring the existing
 *      `useSavedFilters` shape used by the Incidents list).
 *   2. Pass `views` + `activeViewId` + the save/apply/delete callbacks
 *      to this widget.
 *   3. Wire `onApply` to push every captured dimension back into the
 *      DataGrid: search, filters, sort, page size, column controls.
 *
 * The widget is intentionally agnostic to the view's contents — it only
 * cares about `{ id, name, createdAt }`. The caller defines what a view
 * carries (typed via the generic `TView` in the hook).
 */

import React, { useEffect, useRef, useState } from 'react';
import { BookmarkPlus, ChevronDown, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import type { ControlSize } from '../control-styles';
import { Form } from '../form';

export interface SavedView {
  id: string;
  name: string;
  createdAt: string;
}

export interface DataGridSavedViewsProps {
  size?: ControlSize;
  views: ReadonlyArray<SavedView>;
  /** Currently-applied view id (null when no named view is active). */
  activeViewId: string | null;
  /** Apply a saved view — consumer rehydrates DataGrid state from the view. */
  onApply: (id: string) => void;
  /** Delete a saved view by id. */
  onDelete: (id: string) => void;
  /** Save the CURRENT DataGrid state as a new view with the supplied name. */
  onSaveCurrent: (name: string) => void;
  /** Clear the active view marker (returns the grid to "no named view" mode). */
  onClearActive?: () => void;
}

export function DataGridSavedViews({
  size = 'toolbar',
  views,
  activeViewId,
  onApply,
  onDelete,
  onSaveCurrent,
  onClearActive,
}: DataGridSavedViewsProps) {
  const [open, setOpen] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  // Pass-1 P2: reset save-form WHENEVER the popover closes (regardless
  // of close trigger — outside click, trigger button toggle, apply, or
  // clear active). Previously only the outside-click handler reset.
  useEffect(() => {
    if (!open) {
      setShowSaveForm(false);
      setPendingName('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const active = views.find((v) => v.id === activeViewId);
  const label = active ? `View · ${active.name}` : 'Views';

  function handleSave() {
    const trimmed = pendingName.trim();
    if (!trimmed) return;
    onSaveCurrent(trimmed);
    setPendingName('');
    setShowSaveForm(false);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="experience-candidate-data-grid relative min-w-0">
      <Button
        variant="outline"
        size={size}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Saved views"
        className="max-w-full"
      >
        <BookmarkPlus className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="min-w-0 truncate">{label}</span>
        {active && (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-statura-green-accessible"
            aria-hidden="true"
          />
        )}
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
      </Button>
      {open && (
        <div
          role="menu"
          aria-label="Saved views menu"
          className="absolute right-0 z-30 mt-1 w-[min(16rem,calc(100vw-2rem))] rounded-overlay border border-border bg-surface-sheet shadow-popover"
        >
          <div className="max-h-72 overflow-auto p-1">
            {views.length === 0 ? (
              <p className="px-2 py-1.5 text-[11.5px] text-text-muted">
                No saved views yet. Save your current filters + sort + columns to build one.
              </p>
            ) : (
              <ul role="none" className="space-y-0.5">
                {views.map((view) => {
                  const isActive = view.id === activeViewId;
                  return (
                    <li
                      key={view.id}
                      role="none"
                      className={cn(
                        'group flex items-center justify-between gap-1 rounded px-1.5 py-1 text-[11.5px] hover:bg-slate-50',
                        isActive && 'bg-statura-green-accessible/10',
                      )}
                    >
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={isActive}
                        onClick={() => {
                          onApply(view.id);
                          setOpen(false);
                        }}
                        className={cn(
                          'flex-1 truncate text-left',
                          isActive ? 'font-semibold text-statura-ink' : 'text-statura-ink',
                        )}
                      >
                        {view.name}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(view.id);
                        }}
                        aria-label={`Delete ${view.name}`}
                        className="rounded p-0.5 text-text-muted opacity-0 transition group-hover:opacity-100 hover:bg-slate-100 hover:text-red-600 focus:opacity-100"
                      >
                        <Trash2 className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="border-t border-slate-100 p-1.5">
            {showSaveForm ? (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
                className="flex min-w-0 flex-wrap items-center gap-1.5"
              >
                <input
                  autoFocus
                  type="text"
                  value={pendingName}
                  onChange={(e) => setPendingName(e.target.value)}
                  placeholder="View name"
                  aria-label="New view name"
                  className="h-7 min-w-[9rem] flex-1 rounded border border-slate-200 bg-white px-2 text-[11.5px] text-statura-ink placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40"
                />
                <button
                  type="submit"
                  disabled={!pendingName.trim()}
                  className="rounded bg-statura-green-accessible px-2 py-0.5 text-[11px] font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  Save
                </button>
              </Form>
            ) : (
              <button
                type="button"
                onClick={() => setShowSaveForm(true)}
                className="inline-flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[11.5px] text-statura-ink hover:bg-slate-50"
              >
                <BookmarkPlus className="h-3 w-3 shrink-0" aria-hidden="true" />
                Save current as view
              </button>
            )}
            {active && onClearActive && (
              <button
                type="button"
                onClick={() => {
                  onClearActive();
                  setOpen(false);
                }}
                className="mt-1 w-full rounded px-1.5 py-1 text-left text-[10.5px] text-text-muted hover:bg-slate-50 hover:text-statura-ink"
              >
                Clear active view (keep current filters)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
